/**
 * Analytics Public API
 *
 * Usage:
 *   import { track, startSession, endSession, flush } from '@/lib/analytics';
 *   track('screen_view', { screen: 'Home' });
 */

export type { AnalyticsEventName, EventCategory, QueuedEvent } from './types';
export { EVENT_CATEGORIES } from './types';
export { useTrackScreen } from './use-track-screen';

import { analyticsTracker } from './tracker';
import {
  startSession as _startSession,
  endSession as _endSession,
  cleanupSessionListeners as _cleanupListeners,
} from './session';
import type { AnalyticsEventName } from './types';

/** Track an analytics event */
export function track(
  eventName: AnalyticsEventName,
  properties?: Record<string, unknown>,
): void {
  analyticsTracker.track(eventName, properties);
}

/** Set the current user identity */
export function identify(userId: string): void {
  analyticsTracker.identify(userId);
}

/**
 * Apply the account's usage-analytics choice to the tracker's consent gate.
 * `granted` is `!profiles.analytics_opt_out` for `userId`. The AuthProvider
 * calls this whenever the signed-in user's profile is read; the opt-out
 * mutation calls it the moment the switch is turned off. See tracker.ts.
 */
export function setAnalyticsConsent(userId: string, granted: boolean): void {
  analyticsTracker.setConsent(userId, granted);
}

/** Clear identity + queued events on sign-out (prevents post-sign-out RLS
 *  flush spam and cross-user analytics bleed). */
export function resetAnalytics(): void {
  analyticsTracker.reset();
}

/** Start a new session for the user */
export async function startSession(userId: string): Promise<void> {
  return _startSession(userId);
}

/** End the current session */
export async function endSession(userId: string): Promise<void> {
  return _endSession(userId);
}

/** Flush any pending events to Supabase */
export async function flush(): Promise<void> {
  return analyticsTracker.flush();
}

/** Clean up session listeners (call on unmount) */
export function cleanupSessionListeners(): void {
  _cleanupListeners();
}

// Funnel tracking for conversion analysis
export const FUNNELS = {
  ONBOARDING: 'onboarding',
  SCORE_GENERATION: 'score_generation',
  DATA_ROOM_UPLOAD: 'data_room_upload',
  SUBSCRIPTION: 'subscription',
  INVESTOR_DISCOVERY: 'investor_discovery',
} as const;

export function trackFunnel(funnel: string, step: string, metadata?: Record<string, unknown>) {
  track(`funnel_${funnel}_${step}` as AnalyticsEventName, { funnel, step, ...metadata });
}
